/**
 * Phase-5 refinement 6 — save-time meal-plan helpers.
 *
 * Two responsibilities, both invisible to the user under normal conditions:
 *
 *   1. `sanitizeMealPlanForSave(mp)` strips invalid `chefs_special` toggle
 *      states out of the payload BEFORE it hits the backend. A row whose
 *      chef's-special toggle is ON but has no `name` and no
 *      `chefs_special_id` is treated as "toggle slipped on but the user
 *      hasn't picked anything yet" — silently flipped back to OFF so the
 *      backend's Pydantic validator doesn't 422 the entire save.
 *      Returns `{ payload, autoDisabledRestaurants: string[] }` so the
 *      caller can show a lightweight toast listing what changed.
 *
 *   2. `formatValidationError(err)` turns either an Axios error or a raw
 *      Pydantic 422 detail array into a single human-readable string.
 *      No more `[object Object]` and no more JSON dumps in toasts.
 */

/** Return true iff a `chefs_special` block claims to be enabled but has
 *  neither a library id nor a free-text name. The backend rejects this
 *  combination, so we silently disable it client-side. */
function _chefIsInvalid(cs) {
  if (!cs || !cs.enabled) return false;
  const hasName = !!(cs.name || "").trim();
  const hasId = !!cs.chefs_special_id;
  return !hasName && !hasId;
}

/**
 * Clone the meal-plan payload with invalid chef's-special toggles disabled.
 * Returns:
 *   {
 *     payload:                 the cleaned-up meal-plan object,
 *     autoDisabledRestaurants: ["MDR", "Blu", ...]  // for the toast,
 *   }
 */
export function sanitizeMealPlanForSave(mealPlan) {
  if (!mealPlan) return { payload: mealPlan, autoDisabledRestaurants: [] };
  const autoDisabled = [];

  const fixRow = (row) => {
    const cs = row?.chefs_special;
    if (!_chefIsInvalid(cs)) return row;
    autoDisabled.push(row.restaurant_name || row.restaurant_id || "(unknown)");
    return {
      ...row,
      chefs_special: { ...cs, enabled: false, name: "", chefs_special_id: null },
    };
  };

  const next = {
    ...mealPlan,
    regular_restaurants: (mealPlan.regular_restaurants || []).map(fixRow),
    specialty_restaurants: (mealPlan.specialty_restaurants || []).map(fixRow),
  };
  return { payload: next, autoDisabledRestaurants: autoDisabled };
}

/**
 * Map a Pydantic-style location path to a friendly label.
 * Example: ["body","regular_restaurants",2,"chefs_special","name"]
 *       →  'Regular restaurant #3 → Chef\'s Special → Name'
 */
function _formatLoc(loc) {
  if (!Array.isArray(loc) || loc.length === 0) return null;
  const friendly = loc
    .filter((p) => p !== "body")
    .map((p) => {
      if (typeof p === "number") return `#${p + 1}`;
      const map = {
        regular_restaurants:   "Regular restaurant",
        specialty_restaurants: "Specialty restaurant",
        chefs_special:         "Chef's Special",
        chefs_special_id:      "Chef's Special",
        buffets:               "Buffet",
        crew_mess:             "Crew Mess",
        events:                "Event",
        special_events:        "Special Event",
        live_station:          "Live Station",
        theme_buffet_id:       "Theme buffet",
        lines:                 "Buffet line",
        name:                  "Name",
        breakfast_menus:       "Breakfast menus",
        embarkation_lunch:     "Embarkation Lunch",
        chefs_special_id_x:    "id",   // never reached, kept for symmetry
      };
      return map[p] || String(p);
    });
  return friendly.join(" → ");
}

/**
 * Turn either an Axios error object OR a raw Pydantic detail array into a
 * single readable line. Caller always gets a non-empty string.
 */
export function formatValidationError(err) {
  // Axios error → backend body lives at err.response.data
  const data =
    err && typeof err === "object" && err.response
      ? err.response.data
      : err;

  // Pydantic v2 422 → { detail: [ { loc, msg, type }, ... ] }
  if (data && Array.isArray(data.detail)) {
    const first = data.detail[0];
    if (first && typeof first === "object") {
      const where = _formatLoc(first.loc);
      const msg = first.msg || "validation error";
      // Pydantic prefixes with "Value error, " — strip that for readability.
      const clean = msg.replace(/^Value error,\s*/i, "");
      return where ? `${where}: ${clean}` : clean;
    }
  }

  // FastAPI HTTPException → { detail: "string" }
  if (data && typeof data.detail === "string") return data.detail;

  // Plain Error
  if (err && typeof err === "object" && err.message) return err.message;

  // Last resort
  try {
    return typeof data === "string" ? data : JSON.stringify(data);
  } catch {
    return "Save failed";
  }
}
